import { useCallback, useEffect, useState } from 'react';
import { api, CREDITS_CHANGED_EVENT } from '../context/AuthContext';

// Shared across every component on the page: one request per refresh, not one per badge.
let balanceCache = null;
let balanceRequest = null;
let pricesCache = null;
let pricesRequest = null;
const listeners = new Set();

const fetchBalance = () => {
  if (!balanceRequest) {
    balanceRequest = api.get('/billing/credits')
      .then((res) => {
        balanceCache = res.data;
        listeners.forEach((listener) => listener(balanceCache));
        return balanceCache;
      })
      .catch(() => balanceCache)
      .finally(() => { balanceRequest = null; });
  }
  return balanceRequest;
};

const fetchPrices = () => {
  if (pricesCache) return Promise.resolve(pricesCache);
  if (!pricesRequest) {
    pricesRequest = api.get('/billing/credit-prices')
      .then((res) => { pricesCache = res.data.prices || {}; return pricesCache; })
      .catch(() => ({}))
      .finally(() => { pricesRequest = null; });
  }
  return pricesRequest;
};

/** Credits for one action, from the price list (`seconds` for video, `units` e.g. weeks). */
export const creditCost = (prices, feature, { seconds = 0, units = 1 } = {}) => {
  const price = prices?.[feature];
  if (!price) return null;
  if (price.credits_per_second) return price.credits_per_second * Math.max(1, seconds);
  return price.credits * Math.max(1, units);
};

/** Live credit balance (refreshes after AI actions) and the credit price list. */
export default function useCredits() {
  const [credits, setCredits] = useState(balanceCache);
  const [prices, setPrices] = useState(pricesCache);

  const refresh = useCallback(() => fetchBalance(), []);

  useEffect(() => {
    let active = true;
    listeners.add(setCredits);
    fetchBalance();
    fetchPrices().then((p) => { if (active) setPrices(p); });
    const onChanged = () => fetchBalance();
    window.addEventListener(CREDITS_CHANGED_EVENT, onChanged);
    return () => {
      active = false;
      listeners.delete(setCredits);
      window.removeEventListener(CREDITS_CHANGED_EVENT, onChanged);
    };
  }, []);

  return { credits, prices, refresh };
}
